import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../../../../../../app/hooks';
import { USER_TYPE } from '../../../../../../../utils/constants/user';
import { Mixpanel } from '../../../../../../../utils/mixpanel';
import { AICA_ASK_AICA_CITATION_CLICKED } from '../../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { CitationSource } from '../../../../../../../store/dashboard/askAica/citationTypes';
import useFetchDataRoomFiles, { FileClickRouting } from '../../../../../../DataRoom/useFetchDataRoomFiles';

import { resolveCitationAction } from '../components/MessagesContainer/citationNavigation';
import { citationActionTitle } from '../components/MessagesContainer/citationUtils';
import {
    CITATION_ACTION,
    CITATION_SOURCE_TYPE,
    SOURCE_ANCHOR_PREFIX,
    SOURCE_HIGHLIGHT_DURATION_MS,
} from '../components/MessagesContainer/MessagesContainer.constants';
import { CitationSurface, CitationActivateMeta, CitationMessageId } from '../components/MessagesContainer/MessagesContainer.types';
import styles from '../components/MessagesContainer/MessagesContainer.module.scss';

interface ActiveDoc {
    docId: string;
    fileName: string;
}

/** Maps handleFileClick's routing outcome to the analytics delivery dimension —
 * 'error' (deleted/unfetchable doc) is reported as-is rather than dropped, so the
 * event still reflects what actually happened on click. */
const DELIVERY_BY_ROUTING: Record<FileClickRouting, 'drawer' | 'download' | 'error'> = {
    docx: 'drawer',
    preview: 'drawer',
    download: 'download',
    error: 'error',
};

/**
 * Binds `resolveCitationAction` to real effects and owns the ONE Mixpanel emit
 * for every citation click (marker or panel) — the shared handler both
 * CitationMarker and SourcesPanel delegate to, so the two surfaces can never
 * diverge.
 *
 * - document: reuses `useFetchDataRoomFiles.handleFileClick` (F-3/D1) — the exact
 *   Dataroom presigned-fetch + MIME-routing + failure-toast path, mounted here.
 * - section: `window.open` to a named per-org report tab — reused across clicks,
 *   never a same-tab navigate.
 * - conversation: reuses whatever switch-conversation mechanism the active
 *   surface wires up (`openConversation` — leads: resetChatView+
 *   setSelectedConversation+fetchChatHistory; portfolio: loadConversation) —
 *   the SAME mechanism ConversationSidebar's own row click uses, so a citation
 *   click and a sidebar click can never diverge in behaviour.
 * - scroll: today's anchor scrollIntoView + highlight (the universal fallback).
 *
 * Returns NO sheetViewer: `handleFileClick` never sets `showSheetEditor` (only
 * `handleEditSheet` does, which this feature never calls), so a third drawer host
 * would be dead code (OPT-1).
 */
export const useCitationActions = (openConversation?: (conversationId: string, targetMessageId?: string | null) => void) => {
    const params = useParams();
    const userData = useAppSelector((state) => state.user.userData);
    const dashboardConfig = useAppSelector((state) => state.dashboard.dashboardReducer.config);

    const {
        handleFileClick,
        showFile,
        setShowFile,
        showDocxDrawer,
        setShowDocxDrawer,
        fileUrl,
        fileMimeType,
        downloadTheFile,
        downloadingFile,
    } = useFetchDataRoomFiles();
    const [activeDoc, setActiveDoc] = useState<ActiveDoc | null>(null);

    // handleFileClick is a plain const from useFetchDataRoomFiles — a new identity
    // every render. Read it through a ref (kept current via this effect) instead of
    // closing over it directly, so onCitationActivate below keeps ONE stable identity
    // across renders — otherwise CitationAnswer's `components` useMemo (which lists
    // onCitationActivate in its deps) recomputes on every streamed chunk.
    const handleFileClickRef = useRef(handleFileClick);
    useEffect(() => {
        handleFileClickRef.current = handleFileClick;
    }, [handleFileClick]);

    const scrollToSource = useCallback((msgId: CitationMessageId, canonicalId: number) => {
        const anchorId = `${SOURCE_ANCHOR_PREFIX}${msgId}-${canonicalId}`;
        const el = document.getElementById(anchorId);
        if (!el) return;

        // block:'center', NOT 'nearest': the SourcesPanel sits inside the very
        // message bubble whose marker was clicked, so the entry is usually already
        // inside the scrollport — 'nearest' then scrolls NOTHING and the click reads
        // as dead. Centering it in the messages scrollport makes the jump-to-source
        // visible in every case.
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add(styles.SourceHighlight);
        setTimeout(() => {
            el.classList.remove(styles.SourceHighlight);
        }, SOURCE_HIGHLIGHT_DURATION_MS);
    }, []);

    const closeAll = useCallback(() => {
        setShowFile(false);
        setShowDocxDrawer(false);
    }, [setShowFile, setShowDocxDrawer]);

    const onCitationActivate = useCallback(
        async (source: CitationSource, surface: CitationSurface, meta: CitationActivateMeta) => {
            if (source.type === CITATION_SOURCE_TYPE.MODEL_KNOWLEDGE) return;

            const action = resolveCitationAction(source, {
                orgId: params.id,
                isCompanyAccount: userData?.accountType === USER_TYPE.COMPANY,
                dashboardConfig,
            });

            const basePayload = {
                sourceType: source.type,
                surface,
                shownNumber: meta.shownNumber,
                ...(meta.sourcesCount !== undefined ? { sourcesCount: meta.sourcesCount } : {}),
            };

            switch (action.kind) {
                case 'document': {
                    // Citation -> citation: close whatever's already open before pointing the
                    // shared fileUrl/fileMimeType state at the next doc, or the still-mounted
                    // drawer re-renders against the new doc's URL underneath the correct one.
                    closeAll();
                    setActiveDoc({ docId: action.docId, fileName: action.fileName });
                    // Await routing before emitting analytics: a legacy/unsupported type falls
                    // through to download with no drawer, which is a different outcome from
                    // 'open_document' and must not be counted as one.
                    const routing = await handleFileClickRef.current(action.docId, action.fileName);
                    Mixpanel.track(AICA_ASK_AICA_CITATION_CLICKED, {
                        ...basePayload,
                        action: CITATION_ACTION.OPEN_DOCUMENT,
                        delivery: DELIVERY_BY_ROUTING[routing],
                    });
                    return;
                }
                case 'section': {
                    // Named target (not '_blank') so repeated section clicks for the same org
                    // reuse ONE tab instead of stacking a new one per click; the browser's
                    // named-window semantics transparently open a fresh tab if the user closed
                    // the reused one. No `noopener,noreferrer` here on purpose: named-window
                    // reuse requires a shared browsing-context group, and the destination is
                    // our own same-origin app page, so opener isolation isn't needed — a
                    // deliberate, recorded decision.
                    const reportWindow = window.open(action.url, `aica-report-${params.id}`);
                    if (!reportWindow) {
                        // Popup blocked / gesture lost: never leave the click dead — fall back
                        // to the same scroll-to-source every other unreachable section uses.
                        scrollToSource(meta.msgId, meta.canonicalId);
                        Mixpanel.track(AICA_ASK_AICA_CITATION_CLICKED, {
                            ...basePayload,
                            action: CITATION_ACTION.SCROLL_TO_SOURCE,
                            reportSection: action.reportSection,
                            fallbackReason: 'window_blocked',
                        });
                        return;
                    }
                    reportWindow.focus();
                    Mixpanel.track(AICA_ASK_AICA_CITATION_CLICKED, {
                        ...basePayload,
                        action: CITATION_ACTION.NAVIGATE_SECTION,
                        reportSection: action.reportSection,
                    });
                    return;
                }
                case 'conversation': {
                    // No switch-conversation mechanism wired for this surface — fall
                    // back to scroll rather than a silent dead click.
                    if (!openConversation) {
                        scrollToSource(meta.msgId, meta.canonicalId);
                        Mixpanel.track(AICA_ASK_AICA_CITATION_CLICKED, {
                            ...basePayload,
                            action: CITATION_ACTION.SCROLL_TO_SOURCE,
                        });
                        return;
                    }
                    closeAll();
                    openConversation(action.conversationId, action.targetMessageId);
                    Mixpanel.track(AICA_ASK_AICA_CITATION_CLICKED, {
                        ...basePayload,
                        action: CITATION_ACTION.OPEN_CONVERSATION,
                    });
                    return;
                }
                default:
                    scrollToSource(meta.msgId, meta.canonicalId);
                    Mixpanel.track(AICA_ASK_AICA_CITATION_CLICKED, {
                        ...basePayload,
                        action: CITATION_ACTION.SCROLL_TO_SOURCE,
                        ...(action.reportSection !== undefined ? { reportSection: action.reportSection } : {}),
                        ...(action.fallbackReason !== undefined ? { fallbackReason: action.fallbackReason } : {}),
                    });
            }
        },
        [params.id, userData?.accountType, dashboardConfig, scrollToSource, closeAll, openConversation],
    );

    /** Affordance-only tooltip hint for a citation — derived from the RESOLVED
     *  action kind (not merely `report_section` presence), so hover text and click
     *  behaviour can never disagree: 'document' -> "Open document", 'section' ->
     *  the navigation promise (live tab label when available), 'scroll' -> the
     *  plain display_name — no navigation promise for a source that will not
     *  actually navigate (unmapped tab / locked report / config not yet hydrated). */
    const getCitationTitle = useCallback(
        (source: CitationSource): string => {
            const action = resolveCitationAction(source, {
                orgId: params.id,
                isCompanyAccount: userData?.accountType === USER_TYPE.COMPANY,
                dashboardConfig,
            });
            if (action.kind === 'document') return 'Open document';
            if (action.kind === 'section') return citationActionTitle(source, action.sectionLabel);
            return source.display_name;
        },
        [params.id, userData?.accountType, dashboardConfig],
    );

    return {
        onCitationActivate,
        getCitationTitle,
        fileViewer: showFile
            ? {
                  fileName: activeDoc?.fileName ?? '',
                  url: fileUrl,
                  mimeType: fileMimeType,
                  downloading: downloadingFile,
                  onDownload: () => activeDoc && downloadTheFile(activeDoc.docId, activeDoc.fileName),
                  onClose: () => setShowFile(false),
              }
            : null,
        docxViewer: showDocxDrawer
            ? {
                  fileName: activeDoc?.fileName ?? '',
                  fileUrl,
                  downloading: downloadingFile,
                  onDownload: () => activeDoc && downloadTheFile(activeDoc.docId, activeDoc.fileName),
                  onClose: () => setShowDocxDrawer(false),
              }
            : null,
        closeAll,
    };
};

export default useCitationActions;
